import React, { useEffect, useState } from 'react';
import { CheckIcon, ChevronDownIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { isEmpty } from 'lodash';
import { useFormikContext } from 'formik';

import InlineAlert from '@dashboard/components/InlineAlert';
import Button from '@dashboard/components/Button';
import Spinner from '@dashboard/components/Spinner';
import Lozenge from '@dashboard/components/Lozenge';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { formatPriceForInput, formatPrice } from '@dashboard/services/formatting/price';
import getSubscriptionIntervalWithSlash from '@dashboard/services/formatting/getSubscriptionIntervalWithSlash';
import { fetcher } from '@dashboard/services/request';
import { isProduction } from '@dashboard/services/env';
import { sellerPlansKey } from '@dashboard/lib/queryKeys';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';

import { getBillingPeriod } from '../utils/getBillingPeriod';

const SubscriptionPlanSelector = ({ planId, isNew }) => {
  const { sellerId, token, seller, isAdminLoggedInAsSeller } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState();
  const { values, setValues } = useFormikContext();

  const { data: plans, isLoading: isValidating } = useQuery({
    queryKey: sellerPlansKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}/plans`, token),
  });

  useEffect(() => {
    if (selectedPlan) {
      setValues({
        ...values,
        planManagedInStripe: true,
        planId: selectedPlan.id,
        price: formatPriceForInput({
          amount: selectedPlan.amount,
          currency: selectedPlan.currency,
          style: 'decimal',
        }),
        currency: selectedPlan.currency,
        billingPeriod: getBillingPeriod(selectedPlan.interval, selectedPlan.interval_count),
        ...(selectedPlan.trial_period_days && {
          trialPeriodDays: selectedPlan.trial_period_days,
        }),
      });
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (!selectedPlan && planId && !isEmpty(plans)) {
      setSelectedPlan(plans.find(p => p.id === planId));
    }
  }, [planId, plans]);

  return (
    <div>
      <Listbox value={planId} onChange={setSelectedPlan}>
        <div className="flex justify-between">
          <ListboxButton
            className="flex justify-start items-center gap-x-2 w-full relative"
            as={Button}
            type="button"
            appearance="light"
          >
            <div className="text-left w-full">
              {selectedPlan ? (
                <div
                  className={clsx(
                    'flex justify-start items-center w-full min-h-4em text-left first-of-type:rounded-t-sm last-of-type:rounded-b-sm truncate',
                  )}
                >
                  <div>
                    <div className="font-medium">{selectedPlan.product.name}</div>
                    <div className="text-gray-700">
                      {formatPrice({
                        amount: selectedPlan.amount,
                        currency: selectedPlan.currency,
                        style: 'currency',
                      })}{' '}
                      {getSubscriptionIntervalWithSlash(
                        selectedPlan.interval,
                        selectedPlan.interval_count,
                      )}
                      {selectedPlan.trial_period_days &&
                        ` • ${selectedPlan.trial_period_days} day trial`}
                    </div>
                    <div className="space-x-1">
                      <Lozenge>{selectedPlan.product.id}</Lozenge>
                      <Lozenge>{selectedPlan.id}</Lozenge>
                    </div>
                  </div>
                </div>
              ) : (
                'Select price'
              )}
            </div>

            <ChevronDownIcon className="absolute right-2 h-5 w-5" />
          </ListboxButton>
        </div>
        <ListboxOptions>
          <div className={clsx('relative')}>
            <div
              className={clsx(`absolute z-10 mt-2 pb-8`, {
                'w-full': !isNew,
              })}
            >
              <div className="bg-white border border-gray-200 rounded shadow-sm overflow-auto max-h-96">
                {isEmpty(plans) && !isValidating && (
                  <ListboxOption className="p-2">
                    No subscription plans found in your Stripe account
                  </ListboxOption>
                )}

                {isValidating && (
                  <div className="p-2">
                    <Spinner />
                  </div>
                )}

                {plans?.map(plan => (
                  <ListboxOption
                    key={plan.id}
                    value={plan}
                    className={clsx(
                      'flex justify-start items-center w-full min-h-4em text-left p-2 first-of-type:rounded-t-sm last-of-type:rounded-b-sm',
                      {
                        'bg-gray-50 hover:bg-gray-50': plan.id === planId,
                        'hover:bg-gray-100': plan.id !== planId,
                      },
                    )}
                  >
                    <div>
                      <div className="font-medium">{plan.product.name}</div>
                      <div className="text-gray-700">
                        {formatPrice({
                          amount: plan.amount,
                          currency: plan.currency,
                          style: 'currency',
                        })}{' '}
                        {getSubscriptionIntervalWithSlash(plan.interval, plan.interval_count)}
                        {plan.trial_period_days && ` • ${plan.trial_period_days} day trial`}
                      </div>
                      <div className="space-x-1">
                        <Lozenge>{plan.product.id}</Lozenge>
                        <Lozenge>{plan.id}</Lozenge>
                      </div>
                    </div>

                    <div className="flex gap-x-4 ml-auto items-center">
                      <div>
                        {planId === plan.id && <CheckIcon className="w-6 h-6 text-gray-800" />}
                      </div>

                      <Button
                        to={getStripeDashboardUrl(
                          `/${plan.livemode ? '' : 'test/'}prices/${plan.id}`,
                          {
                            seller,
                            isAdminLoggedInAsSeller,
                          },
                        )}
                        target="_blank"
                        appearance="light"
                        onClick={e => e.stopPropagation()}
                      >
                        <ArrowTopRightOnSquareIcon width="16px" />
                      </Button>
                    </div>
                  </ListboxOption>
                ))}
              </div>
            </div>
          </div>
        </ListboxOptions>
      </Listbox>

      {selectedPlan && !selectedPlan.livemode && isProduction && (
        <InlineAlert className="my-2">
          <InlineAlert.Message>
            {`We no longer support Stripe test subscription plans for checkouts.
            Select another subscription plan, turn off "select a plan from
            Stripe" or delete this checkout.`}
          </InlineAlert.Message>
        </InlineAlert>
      )}
    </div>
  );
};

export default SubscriptionPlanSelector;
